import { themeAtom } from "../../shared/atoms";
import { VoyagePNLItem } from "../models/VoyagePnLList.model";
import { formatDateToShortString } from "../utils/UtilFunctions";
import { useAtomValue } from "jotai";
import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

type VoyagepnlListTableProps = {
  data: VoyagePNLItem[] | null | undefined;
};

const formatDate = (value: string | null | undefined) => {
  if (!value) return "";
  return formatDateToShortString(value);
};

const VoyagepnlListTableComponent: React.FC<VoyagepnlListTableProps> = ({ data }) => {
  const theme = useAtomValue(themeAtom);

  const navigate = useNavigate();

  const buildUrl = useCallback(
    (tcCode: string, voyageNumber?: string) => `/voyage/details/${tcCode}/${voyageNumber}/pnl/approval`,
    [],
  );

  const handleRowClick = useCallback(
    (tcCode: string, voyageNumber?: string) => {
      navigate(buildUrl(tcCode, voyageNumber));
    },
    [navigate, buildUrl],
  );

  const rows =
    !data || data.length === 0
      ? null
      : data.map((item) => {
          const {
            tcCode,
            voyageNumber,
            vesselName,
            vesselCode,
            imoNumber,
            oprType,
            commenceDate,
            completeDate,
            status,
            submittedBy,
          } = item;

          return (
            <tr
              key={`${tcCode}-${voyageNumber ?? ""}-${vesselCode ?? ""}`}
              className={twMerge(
                "border-l-2 !border-l-green",
                "cursor-pointer border-b text-left text-xs",
                theme.myVesselsTableRow.row.hover,
                theme.uwhcAssistant.border,
              )}
              onClick={() => handleRowClick(tcCode, voyageNumber)}
            >
              <td className="px-4 py-2 text-left">{tcCode}</td>
              <td className="px-4 py-2 text-center">{voyageNumber ?? "-"}</td>
              <td className="px-4 py-2 text-center">{vesselName ?? "-"}</td>
              <td className="px-4 py-2 text-center">{vesselCode ?? "-"}</td>
              <td className="px-4 py-2 text-center">{imoNumber ?? "-"}</td>
              <td className="px-4 py-2 text-center">{oprType ?? "-"}</td>
              <td className="px-4 py-2 text-center">{formatDate(commenceDate)}</td>
              <td className="px-4 py-2 text-center">{formatDate(completeDate)}</td>
              <td className="px-4 py-2 text-center">{status ?? "-"}</td>
              <td className="px-4 py-2 text-center">{submittedBy ?? "-"}</td>
            </tr>
          );
        });

  if (!data) {
    return <div className={twMerge("p-4 text-sm", theme.uwhcAssistant.text)}>Loading Voyage PnL list…</div>;
  }

  const hasMultipleRows = !!data && data.length > 1;

  return (
    <div
      className={twMerge(
        "min-h-0 flex-1 overflow-auto rounded-lg border",
        theme.myVesselsTable.tailwind.tableBorder,
      )}
    >
      <table
        aria-label="Voyage PnL list"
        className={twMerge(
          "min-w-[1200px] w-full text-center text-[12px]",
          theme.headerComponent.inline.generalText,
        )}
      >
        <thead className={twMerge("sticky top-0 z-10", theme.uwhcAssistant.theadBg)}>
          <tr className={twMerge("h-[44px] border-b", theme.myVesselsTable.tailwind.tableBorder)}>
            <th scope="col" className="h-full whitespace-nowrap px-4 text-left">
              TC CODE
            </th>
            <th scope="col" className="h-full whitespace-nowrap px-4">
              VOYAGE NO
            </th>
            <th scope="col" className="h-full whitespace-nowrap px-4">
              VESSEL NAME
            </th>
            <th scope="col" className="h-full whitespace-nowrap px-4">
              VESSEL CODE
            </th>
            <th scope="col" className="h-full whitespace-nowrap px-4">
              IMO NUMBER
            </th>
            <th scope="col" className="h-full whitespace-nowrap px-4">
              OPR TYPE
            </th>
            <th scope="col" className="h-full whitespace-nowrap px-4">
              COMMENCE DATE
            </th>
            <th scope="col" className="h-full whitespace-nowrap px-4">
              COMPLETE DATE
            </th>
            <th scope="col" className="h-full whitespace-nowrap px-4">
              STATUS
            </th>
            <th scope="col" className="h-full whitespace-nowrap px-4">
              SUBMITTED BY
            </th>
          </tr>
        </thead>

        <tbody className={twMerge(hasMultipleRows ? "last:[&>tr:last-child]:border-b-0" : "")}>
          {!rows || rows.length === 0 ? (
            <tr>
              <td className={twMerge("px-4 py-3 text-left", theme.uwhcAssistant.tableText)} colSpan={10}>
                No data
              </td>
            </tr>
          ) : (
            rows
          )}
        </tbody>
      </table>
    </div>
  );
};

// Memoize the component to avoid re-rendering when props remain the same
const VoyagepnlListTable = React.memo(VoyagepnlListTableComponent);
export default VoyagepnlListTable;
